/**
 * Heatmap READ-ONLY das marcações da pré-análise de triagem (E45.F2). // ux-neutrality:allow doc-comment
 *
 * Reusa os primitivos do heatmap da mesa (E26/E36/E38): a tática de SVG com // ux-neutrality:allow doc-comment
 * viewBox = dimensões naturais da imagem + preserveAspectRatio=meet (mesmo // ux-neutrality:allow doc-comment
 * letterboxing do object-contain), a geometria normalizada à imagem // ux-neutrality:allow doc-comment
 * (lib/annotation_geometry) e a paleta (lib/mesa_annotations). Diferente do // ux-neutrality:allow doc-comment
 * AnnotationLayer da mesa, esta camada é PURAMENTE de exibição — nenhum gesto, // ux-neutrality:allow doc-comment
 * nenhum traço clicável (pointer-events: none). Círculo = região notada; // ux-neutrality:allow doc-comment
 * seta = inclinação. Cor pelo rótulo: convergência (verde) / divergência // ux-neutrality:allow doc-comment
 * (vermelho). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import {
  annotationStrokePx,
  arrowHeadLenPx,
  arrowHeadPoints,
  type ImageSize,
} from '@/lib/annotation_geometry';
import { ANNOTATION_COLOR_HEX, isArrowGeometry } from '@/lib/mesa_annotations';
import type { MarcacaoData, MarcacaoRotulo } from '@/lib/pre_analise';

export interface PreAnaliseMarkerOverlayProps {
  marcacoes: MarcacaoData[];
  /** Dimensões naturais da contestada — testes injetam; produção mede do <img>. */
  naturalSize?: ImageSize | null;
}

const _ROTULO_COLOR: Record<MarcacaoRotulo, string> = {
  convergencia: ANNOTATION_COLOR_HEX.green,
  divergencia: ANNOTATION_COLOR_HEX.red,
};

export function PreAnaliseMarkerOverlay({
  marcacoes,
  naturalSize = null,
}: PreAnaliseMarkerOverlayProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<ImageSize | null>(null);
  const size = naturalSize ?? measured;

  // Mede as dimensões naturais do <img> irmão (produção; testes injetam) —
  // mesma tática do AnnotationLayer.
  useEffect(() => {
    if (naturalSize !== null) return undefined;
    const img = wrapperRef.current?.parentElement?.querySelector('img') ?? null;
    if (img === null) return undefined;
    function measure(): void {
      if (img !== null && img.naturalWidth > 0) {
        setMeasured({ width: img.naturalWidth, height: img.naturalHeight });
      }
    }
    measure();
    img.addEventListener('load', measure);
    return () => img.removeEventListener('load', measure);
  }, [naturalSize]);

  return (
    <div ref={wrapperRef} className="annotation-layer" data-testid="pre-analise-markers">
      {size !== null && (
        <svg
          className="annotation-surface"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={ptBR.preAnalise.heatmapLabel}
        >
          {marcacoes.map((marcacao, idx) => {
            const color = _ROTULO_COLOR[marcacao.rotulo];
            const stroke = annotationStrokePx(size);
            const geometry = marcacao.regiao;
            if (isArrowGeometry(geometry)) {
              const headLen = arrowHeadLenPx(size);
              return (
                <g key={idx} data-testid={`pre-analise-marker-${idx}`} data-tipo="seta">
                  <line
                    x1={geometry.x1 * size.width}
                    y1={geometry.y1 * size.height}
                    x2={geometry.x2 * size.width}
                    y2={geometry.y2 * size.height}
                    stroke={color}
                    strokeWidth={stroke}
                  />
                  <polygon points={arrowHeadPoints(geometry, size, headLen)} fill={color} />
                  <title>{marcacao.observacao}</title>
                </g>
              );
            }
            return (
              <g key={idx} data-testid={`pre-analise-marker-${idx}`} data-tipo="circulo">
                <circle
                  cx={geometry.cx * size.width}
                  cy={geometry.cy * size.height}
                  r={geometry.r * size.width}
                  fill="none"
                  stroke={color}
                  strokeWidth={stroke}
                />
                <title>{marcacao.observacao}</title>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
